import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { SKIP_NOTIFICATION } from '~core/interceptors/error-handler.interceptor';
import { AccountType, BaseAccount, QuotationSettings } from '~modules/auth/types/account.types';
import { InternalUser, ExternalUser } from "~modules/auth/types/user.types";

import { environment } from '../../../environments/environment';

@Injectable({
	providedIn: 'root',
})
export class AccountService {
	/** Account types per account id; an account's type only changes through {@link updateAccount}. */
	private readonly accountTypes = new Map<string, Observable<AccountType>>();

	constructor(
		private readonly http: HttpClient
	) {}

	/** Checks a general email against the account rule for the current user, without saving it. */
	checkGeneralEmail(generalEmail: string): Observable<void> {
		return this.http.post<void>(`${environment.accountServiceUrl}/account/generalemail/check`, { generalEmail }, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
		});
	}

	createUser(token: string, user: any): Observable<any> {
		const params: HttpParams = new HttpParams();
		let headers: HttpHeaders = new HttpHeaders();
		headers = headers.append('Content-Type', 'application/json');
		headers = headers.append('Authorization', `Bearer ${token}`);
		return this.http.post<any> (`${environment.accountServiceUrl}/user`, user, {
			params,
			headers
		})
			.pipe(map(response => response.user));

	}

	public getUser(userId: string): Observable<InternalUser | ExternalUser> {
		const encodedUserId = encodeURIComponent(userId);
		// The caller (AuthService) says what a failure means; the generic toast would not.
		return this.http.get<InternalUser | ExternalUser>(`${environment.accountServiceUrl}/user/${encodedUserId}`, {
			context: new HttpContext().set(SKIP_NOTIFICATION, true),
		})
			.pipe(
				switchMap((user) => {
					if (!(user as ExternalUser).accountId) {
						return of(user);
					}

					return this.http.get(`${environment.accountServiceUrl}/account/${(user as ExternalUser).accountId}`)
						.pipe(map((account) => ({
							...user,
							account
						}) as any))
				})
			);
	}

	public updateUser(userId: string, body): Observable<InternalUser | ExternalUser> {
		return this.http.put<InternalUser | ExternalUser>(`${environment.accountServiceUrl}/user/${encodeURIComponent(userId)}`, body);
	}

	public createExternalUser(userId: string, body): Observable<InternalUser | ExternalUser> {
		// encodeURIComponent — userId is an email, and a "+" in the path is
		// silently decoded as a space by some backends (Flask/FastAPI), causing
		// the user lookup to miss and return "This endpoint can only be called
		// to create a registered external user".
		return this.http.post<ExternalUser>(`${environment.accountServiceUrl}/user/${encodeURIComponent(userId)}/external`, body);
	}

	public createInternalUser(userId: string, body): Observable<InternalUser | ExternalUser> {
		return this.http.post<InternalUser>(`${environment.accountServiceUrl}/user/${encodeURIComponent(userId)}/internal`, body);
	}

	public getAccounts(): Observable<BaseAccount[]> {
		return this.http.get<BaseAccount[]>(`${environment.accountServiceUrl}/account`);
	}

	public getAccount(accountId: string): Observable<BaseAccount> {
		return this.http.get<BaseAccount>(`${environment.accountServiceUrl}/account/${accountId}`);
	}

	/** The type of the account a project belongs to, which decides the quote features on offer. */
	public getAccountType(accountId: string): Observable<AccountType> {
		if (!this.accountTypes.has(accountId)) {
			this.accountTypes.set(accountId, this.getAccount(accountId).pipe(
				map((account) => account.accountType),
				shareReplay(1)
			));
		}
		return this.accountTypes.get(accountId);
	}

	public updateAccount(accountId: string, body): Observable<InternalUser | ExternalUser> {
		return this.http.put<InternalUser | ExternalUser>(`${environment.accountServiceUrl}/account/${accountId}`, body)
			.pipe(tap(() => this.accountTypes.delete(accountId)));
	}

	/**
	 * Replaces the account's white-label quotation settings. Separate from {@link updateAccount}
	 * because that PUT is admin-only, while the people drafting quotations are regular
	 * account users; this endpoint is open to them and touches nothing but the settings.
	 */
	public updateQuotationSettings(accountId: string, settings: QuotationSettings): Observable<BaseAccount> {
		return this.http.put<BaseAccount>(`${environment.accountServiceUrl}/account/${accountId}/quotationsettings`, settings);
	}
}
